import type { GameVersion } from '../lib/gameVersion'
// Player status from raid-helper import
export type PlayerStatus = 'primary' | 'bench' | 'absence' | 'tentative'

// WoW Classes
export type WowClass =
  | 'Warrior' | 'Paladin' | 'Hunter' | 'Rogue'
  | 'Priest' | 'Shaman' | 'Mage' | 'Warlock' | 'Druid'
  | 'Death Knight'

// Role types
export type RoleName = 'Tank' | 'Melee' | 'Ranged' | 'Healer'

// Raid types
export type RaidShortName =
  // Turtle WoW (archived)
  | 'bwl' | 'aq40' | 'naxx' | 'kara40'
  // Wrath of the Lich King
  | 'naxx10'    | 'naxx25'
  | 'os10'      | 'os25'
  | 'eoe10'     | 'eoe25'
  | 'onyxia10'  | 'onyxia25'
  | 'voa10'     | 'voa25'
  | 'ulduar10'  | 'ulduar25'
  | 'totc10'    | 'totc25'    | 'totc10h'  | 'totc25h'
  | 'icc10'     | 'icc25'     | 'icc10h'   | 'icc25h'
  | 'rs10'      | 'rs25'      | 'rs10h'    | 'rs25h'

// Import JSON structure from raid-helper Discord bot
export interface RaidHelperSignUp {
  entryTime?: number
  specName: string
  name: string
  roleName: string
  className: string
  userId: string
  status: PlayerStatus
}

export interface RaidHelperImport {
  date: string
  title: string
  signUps: RaidHelperSignUp[]
}

// Database types
export interface Raid {
  id: string
  name: string
  short_name: RaidShortName
  sort_order: number
  player_count: number  // 10, 20, 25, 40
  /** A DKP raid: social members can't take part */
  is_dkp_raid?: boolean
  is_active: boolean
  game_version?: string
  requires_attunement: boolean  // Gates entry behind an attunement chain
  created_at: string
}

// One row per (character, raid) that IS attuned - absence means "not
// attuned". Attunement is per character, not per account: an alt is not
// attuned just because its main is.
export interface PlayerAttunement {
  id: string
  player_profile_id: string
  raid_id: string
  attuned_at: string
  set_by: string | null
}

export interface Boss {
  id: string
  raid_id: string
  name: string
  sort_order: number
  notes: string | null
  turtle_boss_id: number | null  // Deprecated, use turtle_boss_ids
  turtle_boss_ids: number[]      // Array of NPC IDs for multi-boss encounters
  video_url: string | null       // YouTube or other video link for strategy
  kill_video_url: string | null  // Guild's own kill video
  strategy_notes: string | null  // Detailed strategy notes for this boss
  dkp_value: number | null       // DKP awarded for killing this boss
  trigger_name: string | null    // NPC name that triggers kill detection (if different from boss name)
  lust_timing: string | null     // When to pop Bloodlust (e.g. "Pull", "Phase 2", "30% HP")
  lust_player: string | null    // Character name of the Shaman who pops it
  game_version?: string
  created_at: string
}

export interface BossImage {
  id: string
  boss_id: string
  image_url: string
  title: string | null
  sort_order: number
  created_at: string
}

export interface RaidEvent {
  id: string
  raid_id: string | null
  event_date: string
  title: string | null
  imported_json: RaidHelperImport | null
  status?: string
  game_version?: string
  combat_log_url?: string | null
  bench_window_opened_at?: string | null
  bench_window_closed_at?: string | null
  eor_window_opened_at?: string | null
  eor_window_closed_at?: string | null
  bench_dkp?: number
  dkp_issued_per_raider?: number | null
  created_at: string
}

export interface RaidEventRaid {
  id: string
  raid_event_id: string
  raid_id: string
  sort_order: number
  combat_log_url: string | null
  created_at: string
}

export interface RaidEventPlayer {
  id: string
  raid_event_id: string
  discord_user_id: string
  character_name: string
  class_name: string | null
  spec_name: string | null
  role_name: string | null
  status: PlayerStatus
  attendance_status?: string
  join_timing?: 'start' | 'late'
  eor_submitted?: boolean
  excuse_reason?: string
  promoted_at?: string
  // Per-raid overrides (temporary role changes for specific raids)
  role_override?: string | null
  class_override?: string | null
  spec_override?: string | null
  created_at: string
}

export interface BenchHistory {
  id: string
  raid_event_id: string
  discord_user_id: string
  character_name: string
  reason: string | null
  benched_by: string | null
  created_at: string
}

export interface PlayerBenchStats {
  id: string
  discord_user_id: string
  character_name: string
  total_times_benched: number
  times_benched_30d: number
  times_benched_60d: number
  times_benched_90d: number
  last_benched_at: string | null
  total_raids_attended: number
  updated_at: string
}

// UI-specific types
export interface PlayerWithBenchStats extends RaidEventPlayer {
  bench_stats?: PlayerBenchStats
}

export interface BenchCandidate {
  discord_user_id: string
  character_name: string
  class_name: string
  role_name: string
  bench_score: number
  last_benched_at: string | null
  times_benched_30d: number
  times_benched_90d: number
}

// Group colors for assignments
export const GROUP_COLORS = ['Red', 'Blue', 'Yellow', 'Pink', 'Green', 'Orange'] as const
export type GroupColor = typeof GROUP_COLORS[number]

// Attendance status for players
export type AttendanceStatus =
  | 'signed'           // Signed up, raid not yet happened
  | 'attended'         // Was in the raid
  | 'benched'          // Benched, outcome not yet known
  | 'benched_attended' // Was benched but came to raid (standby)
  | 'benched_absent'   // Was benched and did not come
  | 'late'             // Came but was late
  | 'no_show'          // Signed up but didn't show
  | 'excused'          // Notified absence in advance
  | 'unsigned_attended' // Didn't sign up but showed up anyway

// Raid event status
export type RaidEventStatus = 'planning' | 'in_progress' | 'completed' | 'cancelled'
export type RaidStatus = 'planning' | 'ready' | 'in_progress' | 'completed'

// Guild ranks
export type GuildRank = 'Guild Master' | 'Officer' | 'Raven' | 'Member' | 'Alt' | 'Trial'

// DKP import log
export interface DkpImport {
  id: string
  imported_at: string
  imported_by: string | null
  players_updated: number
  total_dkp: number
  notes: string | null
}

// DKP name mapping (links DKP export names to roster players)
export interface DkpNameMapping {
  id: string
  dkp_name: string
  player_profile_id: string
  created_at: string
}

// Item quality for loot
export type ItemQuality = 'legendary' | 'epic' | 'rare' | 'uncommon' | 'common'

// Crafting professions (defined early for use in LootItem)
export type Profession = 'Alchemy' | 'Blacksmithing' | 'Cooking' | 'Enchanting' |
  'Engineering' | 'First Aid' | 'Jewelcrafting' |
  'Leatherworking' | 'Survival & Gardening' | 'Tailoring' |
  'Mining' | 'Herbalism' | 'Skinning'

/**
 * The professions the guild actually plays.
 *
 * Jewelcrafting and Survival & Gardening are not in WoW Forever - the first
 * arrived with a later expansion, the second was a Turtle WoW addition - so
 * they are not offered. They stay in the `Profession` type above because old
 * items and recipes are still tagged with them, and archived data should
 * remain readable rather than become untypeable.
 */
export const ALL_PROFESSIONS: Profession[] = [
  'Alchemy', 'Blacksmithing', 'Cooking', 'Enchanting',
  'Engineering', 'First Aid',
  'Leatherworking', 'Tailoring',
  // Gathering professions craft too on WoW Forever: smelting, and a few new
  // recipes for Herbalism and Skinning
  'Mining', 'Herbalism', 'Skinning',
]

// Item categories for organization
export type ItemCategory = 'crafted' | 'material' | 'raid_loot'

export const ALL_ITEM_CATEGORIES: ItemCategory[] = ['crafted', 'material', 'raid_loot']

export const ITEM_CATEGORY_LABELS: Record<ItemCategory, string> = {
  crafted: 'Crafted',
  material: 'Material',
  raid_loot: 'Raid Loot',
}

// Item slots (parsed from tooltip)
export type ItemSlot = 'Head' | 'Neck' | 'Shoulder' | 'Back' | 'Chest' | 'Shirt' | 'Tabard' | 'Wrist' | 'Hands' | 'Waist' | 'Legs' | 'Feet' | 'Finger' | 'Trinket' | 'Main Hand' | 'Off Hand' | 'One-Hand' | 'Two-Hand' | 'Ranged' | 'Relic' | 'Held In Off-hand'

// Item subtypes (armor/weapon types parsed from tooltip)
export type ItemSubtype = 'Cloth' | 'Leather' | 'Mail' | 'Plate' | 'Sword' | 'Axe' | 'Mace' | 'Dagger' | 'Fist Weapon' | 'Polearm' | 'Staff' | 'Bow' | 'Gun' | 'Crossbow' | 'Wand' | 'Thrown' | 'Shield' | 'Libram' | 'Idol' | 'Totem'

// Item binding types
export type ItemBinding = 'bop' | 'boe' | 'bou'

// Item stats JSON structure (flexible, captures all stats)
export interface ItemStats {
  // Core stats
  armor?: number
  block?: number
  stamina?: number
  intellect?: number
  spirit?: number
  strength?: number
  agility?: number
  // Weapon stats
  damageMin?: number
  damageMax?: number
  speed?: number
  dps?: number
  // Equip effects (parsed)
  attackPower?: number
  spellPower?: number
  healingPower?: number
  mp5?: number
  spellCrit?: number
  spellHit?: number
  meleeCrit?: number
  meleeHit?: number
  defense?: number
  dodge?: number
  parry?: number
  blockChance?: number
  blockValue?: number
  spellPen?: number
  // Raw effects we couldn't parse into specific fields
  effects?: string[]
  // Any other stats we find
  [key: string]: number | string | string[] | undefined
}

// Loot item (with optional Turtle WoW database link)
export interface LootItem {
  id: string
  name: string
  game_version?: string
  turtle_item_id: number | null
  quality: ItemQuality | null
  tooltip_image_url: string | null
  tooltip_html?: string | null       // WoW-style tooltip HTML — lazy-loaded on hover
  item_level: number | null
  item_slot: ItemSlot | null
  item_subtype: ItemSubtype | null  // Parsed from tooltip (Cloth, Plate, Sword, etc.)
  item_binding: ItemBinding | null  // Parsed from tooltip (bop, boe, bou)
  item_stats: ItemStats | null      // All stats as JSON (flexible, nothing lost)
  // Category and profession
  item_category: ItemCategory | null
  profession: Profession | null
  times_crafted: number
  is_tradable: boolean
  is_apothecary_craft: boolean    // Item will be crafted by the Apothecary
  is_materials_buying: boolean    // Item should appear on materials buying list
  created_at: string
  // Addon import tracking
  data_source: string | null       // 'legacy' or 'addon' - tracks where data came from
  icon_name: string | null         // Icon filename from addon (e.g., "inv_sword_04")
  addon_updated_at: string | null  // When item was last updated from addon
  // Pricing
  vendor_price: number | null      // Per-unit vendor buy price in copper
  // Auction house prices (only for tradable items)
  ah_price_avg: number | null      // Average price in copper (7-day average)
  ah_price_min: number | null      // Minimum buyout in copper (latest)
  ah_qty_available: number | null  // Quantity available on AH (latest)
  ah_price_updated_at: string | null // When price was last fetched
}

// Loot awarded in a raid
export interface RaidLoot {
  id: string
  raid_event_id: string
  loot_item_id: string
  boss_id: string | null
  boss_name: string | null
  player_profile_id: string | null
  awarded_to_name: string
  notes: string | null
  dkp_spent: number | null           // DKP paid for item (from SOTA auction)
  bid_type: 'MS' | 'OS' | null       // Main spec or Off spec
  sota_auction_id: string | null     // SOTA auction ID for audit trail
  created_at: string
}

// Extended raid loot with joined data
export interface RaidLootWithDetails extends RaidLoot {
  loot_item: LootItem
  player_profile?: PlayerProfile
  raid_event?: RaidEvent
  boss?: Boss
}

// Priority spec options for loot
export type PrioritySpec = 'All' | 'Tanks' | 'Healers' | 'Casters' | 'Physical DPS' | 'Physical DPS & Tanks' | 'Casters & Healers'

export const PRIORITY_SPECS: PrioritySpec[] = [
  'All',
  'Tanks',
  'Healers',
  'Casters',
  'Physical DPS',
  'Physical DPS & Tanks',
  'Casters & Healers'
]

// Priority role for player profiles (subset of PrioritySpec, no combo values)
export type PriorityRole = 'Tanks' | 'Healers' | 'Casters' | 'Physical DPS'

export const PRIORITY_ROLES: PriorityRole[] = ['Tanks', 'Healers', 'Casters', 'Physical DPS']

// Item boss source - tracks which bosses can drop an item
export interface ItemBossSource {
  id: string
  loot_item_id: string
  boss_id: string
  drop_chance: string | null
  priority_spec: PrioritySpec | null
  priority_notes: string | null  // Special priority rules like "Rogues only", "Roll 80+"
  notes: string | null
  created_at: string
}

// Extended item with boss sources
export interface LootItemWithSources extends LootItem {
  boss_sources?: (ItemBossSource & { boss: Boss })[]
}

export interface TalentBuild {
  name: string
  url: string
}

// Player profile (master list of known players)
export interface PlayerProfile {
  id: string
  character_name: string
  discord_user_id: string | null
  class_name: string
  role_name: string
  spec_name: string | null
  race: string | null
  faction: 'Horde' | 'Alliance' | null
  notes: string | null
  is_active: boolean
  is_main: boolean
  main_profile_id: string | null  // Links alt characters to their main character
  priority_role: PriorityRole | null
  dkp: number
  guild_rank: GuildRank | null
  last_online: string | null
  game_version: GameVersion
  /** Whether this character wants a raid spot or is here to be social */
  membership_intent?: 'raider' | 'social' | null
  twow_character_name: string | null
  os_spec: string | null
  /** What the off-spec plays as. Optional: added with Forever registration. */
  os_role?: string | null
  /** Battle.net BattleTag ("Name#1234"), members only (migration 149) */
  battletag?: string | null
  talent_builds?: TalentBuild[] | null
  created_at: string
  updated_at: string
}

export type CharacterApplicationStatus = 'pending' | 'approved' | 'rejected'

export interface CharacterApplication {
  id: string
  user_account_id: string
  discord_user_id: string
  character_name: string
  class_name: string
  spec_name: string | null
  /** The off-spec they intend to play, carried through to the profile */
  os_spec: string | null
  /** What that off-spec plays as - "Feral" alone does not say tank or melee */
  os_role?: string | null
  role_name: string
  race: string | null
  faction: 'Horde' | 'Alliance' | null
  /** What they went by on Turtle WoW - a lot of people are still known by it */
  twow_character_name: string | null
  notes: string | null
  /** Which era this application is for - members register once per server */
  game_version: GameVersion
  /** Raid spot wanted, or here to be social */
  membership_intent: 'raider' | 'social' | null
  status: CharacterApplicationStatus
  officer_notes: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  created_player_profile_id: string | null
  created_at: string
  updated_at: string
  // Joined
  user_account?: { discord_username: string; discord_avatar: string | null }
}

// Extended raid event with status
export interface RaidEventExtended extends RaidEvent {
  status: RaidEventStatus
  finalized_at: string | null
  finalized_by: string | null
}

// Extended raid event player with attendance
export interface RaidEventPlayerExtended extends RaidEventPlayer {
  attendance_status: AttendanceStatus
  attendance_notes: string | null
  arrived_late: boolean
  late_minutes: number | null
  updated_at: string
}

// Player statistics (aggregated)
export interface PlayerStatistics {
  id: string
  discord_user_id: string
  total_raids_signed: number
  total_raids_attended: number
  total_raids_benched: number
  total_raids_benched_but_came: number
  total_raids_late: number
  total_raids_no_show: number
  total_raids_excused: number
  attendance_rate: number
  last_raid_date: string | null
  last_attended_date: string | null
  first_raid_date: string | null
  streak_current: number
  streak_best: number
  updated_at: string
}

// Player with all related data for display
export interface PlayerWithStats extends RaidEventPlayer {
  profile?: PlayerProfile
  statistics?: PlayerStatistics
  bench_stats?: PlayerBenchStats
}

// All WoW classes
export const ALL_CLASSES: WowClass[] = [
  'Warrior', 'Paladin', 'Hunter', 'Rogue', 'Priest',
  'Shaman', 'Mage', 'Warlock', 'Druid'
]

// All roles
export const ALL_ROLES: RoleName[] = ['Tank', 'Healer', 'Melee', 'Ranged']

// TWoW-ruleset races (octowow.st)
// WoW Forever's races. Skyborn appear on both sides, in the slot High Elf
// and Goblin used to hold.
// WoW Forever's races. The Skyborne replace High Elf and Goblin, and each
// side has its own: High Order for the Alliance, Windshaper for the Horde.
export const ALLIANCE_RACES = ['Human', 'Dwarf', 'Night Elf', 'Gnome', 'High Order Skyborne'] as const
export const HORDE_RACES = ['Orc', 'Undead', 'Tauren', 'Troll', 'Windshaper Skyborne'] as const
export const ALL_RACES = [...ALLIANCE_RACES, ...HORDE_RACES]

export function getFactionFromRace(race: string): 'Horde' | 'Alliance' | null {
  if ((ALLIANCE_RACES as readonly string[]).includes(race)) return 'Alliance'
  if ((HORDE_RACES as readonly string[]).includes(race)) return 'Horde'
  return null
}

// ============================================
// Boss Types
// ============================================

// Extended boss with raid info
export interface BossWithAssignments extends Boss {
  raid?: Raid
}

// Official WoW Raid Marker type (numbered 1-8 in-game order)
export const RAID_MARKS = ['star', 'circle', 'diamond', 'triangle', 'moon', 'square', 'cross', 'skull'] as const
export type RaidMark = typeof RAID_MARKS[number]

// Raid marker display info (official WoW colors and icons)
export const RAID_MARKERS: Record<RaidMark, { name: string; color: string; icon: string }> = {
  star: { name: 'Star', color: '#FFD100', icon: '/icons/raid-markers/star.png' },
  circle: { name: 'Circle', color: '#FF7D0A', icon: '/icons/raid-markers/circle.png' },
  diamond: { name: 'Diamond', color: '#A335EE', icon: '/icons/raid-markers/diamond.png' },
  triangle: { name: 'Triangle', color: '#1EFF00', icon: '/icons/raid-markers/triangle.png' },
  moon: { name: 'Moon', color: '#69CCF0', icon: '/icons/raid-markers/moon.png' },
  square: { name: 'Square', color: '#0070DD', icon: '/icons/raid-markers/square.png' },
  cross: { name: 'Cross', color: '#C41F3B', icon: '/icons/raid-markers/cross.png' },
  skull: { name: 'Skull', color: '#FFFFFF', icon: '/icons/raid-markers/skull.png' },
}

// Legacy export for backwards compatibility
export const RAID_MARK_ICONS: Record<RaidMark, string> = Object.fromEntries(
  RAID_MARKS.map(mark => [mark, RAID_MARKERS[mark].icon])
) as Record<RaidMark, string>

// Custom uploaded icon for assignments
export interface AssignmentIcon {
  id: string
  name: string
  image_url: string
  created_at: string
}

// Icon settings for Discord export and assignment availability
export interface IconSetting {
  id: string
  icon_key: string           // 'skull', 'star', 'custom:uuid', etc.
  display_name: string       // 'Skull', 'Star', custom name
  icon_type: 'raid_marker' | 'custom'
  image_url: string | null   // URL to the icon image
  is_enabled: boolean        // Whether available for assignments
  discord_emoji: string | null  // Discord emoji like '<:skull:123>' or ':skull:'
  sort_order: number
  created_at: string
  updated_at: string
}

// ============================================
// Flexible Group Configuration System (DEPRECATED)
// These types are kept for backwards compatibility during migration
// ============================================

// Group preset colors for flexible group system
export const GROUP_PRESET_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' },
]

// Boss group configuration (customizable per boss)
export interface BossGroupConfig {
  id: string
  boss_id: string
  group_name: string
  slot_count: number
  color: string
  marker: RaidMark | null
  sort_order: number
  created_at: string
  updated_at: string
}

// Default assignment within a group (boss-level template)
export interface BossGroupAssignment {
  id: string
  group_config_id: string
  slot_number: number
  player_profile_id: string | null
  notes: string | null
  created_at: string
  updated_at: string
  // Joined data
  player_profile?: PlayerProfile
}

// Raid-specific group assignment
export interface RaidGroupAssignment {
  id: string
  raid_event_id: string
  group_config_id: string
  slot_number: number
  player_id: string | null
  notes: string | null
  created_at: string
  updated_at: string
  // Joined data
  player?: RaidEventPlayer
}

// Group config with assignments for display
export interface BossGroupConfigWithAssignments extends BossGroupConfig {
  assignments: BossGroupAssignment[]
}

// Group config with raid assignments for display
export interface BossGroupConfigWithRaidAssignments extends BossGroupConfig {
  raid_assignments: RaidGroupAssignment[]
}

// ============================================
// Boss Templates System (v1.7.0)
// Simple group layouts per boss like Raid Planner
// ============================================

// A template is a named group layout for a boss
export interface BossTemplate {
  id: string
  boss_id: string
  name: string  // "Shard Groups", "Sides", "Tank Rotation"
  group_count: number
  slots_per_group: number
  layout: 'grid' | 'columns'
  image_url: string | null  // URL to explanatory image (e.g., positioning diagram)
  is_raid_groups?: boolean  // Mark as the raid groups template for addon export
  created_at: string
  updated_at: string
}

// Custom settings per group within a template
export interface BossTemplateGroup {
  id: string
  template_id: string
  group_number: number  // 1, 2, 3...
  name: string | null  // Custom name, defaults to "Group X"
  icon: string | null  // Optional raid marker or custom icon
  color: string | null  // Optional border color (e.g., '#ef4444')
  slot_count: number | null  // Override slots for this specific group
}

// Default player assignment in a template (boss-level)
export interface BossTemplateAssignment {
  id: string
  template_id: string
  group_number: number
  slot_number: number
  player_profile_id: string | null
  // Joined data
  player_profile?: PlayerProfile
}

// Slot-level note (independent of player assignment)
export interface BossTemplateSlotNote {
  id: string
  template_id: string
  group_number: number
  slot_number: number
  note: string | null
  icon: string | null  // raid marker key (e.g., 'skull') or custom icon ID
}

// Raid-specific player assignment (copied from template)
export interface RaidTemplateAssignment {
  id: string
  raid_event_id: string
  template_id: string
  group_number: number
  slot_number: number
  player_id: string | null
  // Joined data
  player?: RaidEventPlayer
}

// Template with all related data for display
export interface BossTemplateWithGroups extends BossTemplate {
  groups: BossTemplateGroup[]
  assignments: BossTemplateAssignment[]
  slot_notes: BossTemplateSlotNote[]
}

// Template with raid-specific assignments
export interface BossTemplateWithRaidAssignments extends BossTemplate {
  groups: BossTemplateGroup[]
  raid_assignments: RaidTemplateAssignment[]
}

// ============================================
// Discord Bot Integration Types (v1.8.0)
// ============================================

// Discord message types
export type DiscordMessageType =
  | 'bench_list'        // Manual: List of benched players
  | 'bench_open'        // Scheduled: Bench window opens
  | 'bench_close'       // Scheduled: Bench window closes
  | 'eor_open'          // Scheduled: End-of-raid window opens
  | 'eor_close'         // Scheduled: End-of-raid window closes
  | 'signup_closed'     // Scheduled: Signups are closed
  | 'tonight_setup'     // Manual: Tonight's setup announcement
  | 'assignments_posted'// Manual: Assignments have been posted
  | 'assignments'       // Manual: Full assignments content
  | 'call_in'           // Manual: Call in a bench player
  | 'signup_reminder'   // Manual: Reminder to sign up before signups close

// Discord channel types
export type DiscordChannelType = 'bench' | 'signup' | 'assignments' | 'general'

// Scheduled message status
export type DiscordMessageStatus = 'pending' | 'sent' | 'failed' | 'cancelled'

// Discord bot configuration (singleton)
export interface DiscordBotConfig {
  id: string

  // Channel IDs
  bench_channel_id: string | null
  signup_channel_sunday: string | null
  signup_channel_tuesday: string | null
  signup_channel_thursday: string | null
  assignments_channel_id: string | null
  general_channel_id: string | null
  apothecary_channel_id: string | null  // For materials buying announcements
  dungeon_runs_channel_id?: string | null  // Dungeon finder posts (migration 116)
  dkp_channel_id?: string | null  // DKP+ upload summaries (migration 137)
  dungeon_runs_ping?: string | null  // '@everyone', '@here', '<@&role>' or '' (migration 118)

  // Fixed raid schedule (same every raid) - CET timezone
  raid_start_time: string  // TIME as string "HH:MM:SS"
  raid_end_time: string
  signup_close_time?: string  // Deprecated: signup messages are manual now
  timezone: string

  // Bench window timing (minutes)
  bench_open_before: number
  bench_close_after: number
  eor_open_before: number
  eor_close_after: number

  // Feature toggles
  auto_schedule_enabled: boolean
  show_app_link_after_push: boolean  // Show "View assignments on app" message after Push All

  // Buying list
  buying_list_channel_id: string | null  // Channel for buying list announcements
  buying_list_cod_character?: string | null  // Who materials are COD'd to (migration 141)
  buying_list_message_id: string | null  // Last message ID (for delete/replace)

  // Raid Bingo (Easter Egg)
  bingo_channel_id: string | null        // Channel for bingo winner announcements
  bingo_quotes: string | null            // Comma-separated quotes for bingo cards
  bingo_gold_reward: number | null       // Gold reward amount for bingo winner (default 100)

  // Crafting system
  crafting_channel_id: string | null     // Channel for craft request notifications
  crafting_reaction_emoji: string | null // Reaction emoji for craft requests (e.g., "white_check_mark")

  created_at: string
  updated_at: string
}

// Scheduled message in queue
export interface DiscordScheduledMessage {
  id: string
  raid_event_id: string

  message_type: DiscordMessageType
  channel_type: DiscordChannelType

  scheduled_for: string  // TIMESTAMPTZ as ISO string
  status: DiscordMessageStatus
  sent_at: string | null
  error_message: string | null

  created_at: string
  updated_at: string

  // Joined data
  raid_event?: RaidEvent
}

// Message history log entry
export interface DiscordMessageLog {
  id: string
  raid_event_id: string | null
  channel_id: string
  message_type: DiscordMessageType
  content_preview: string | null
  sent_at: string
  sent_by: string | null
  created_at: string

  // Joined data
  raid_event?: RaidEvent
}

// Discord embed for rich messages
export interface DiscordEmbed {
  title?: string
  description?: string
  color?: number  // Decimal color value
  image?: { url: string }
  thumbnail?: { url: string }
  fields?: Array<{
    name: string
    value: string
    inline?: boolean
  }>
  footer?: { text: string }
  timestamp?: string
}

// Payload for sending Discord messages
export interface DiscordMessagePayload {
  channel_id: string
  content?: string
  embeds?: DiscordEmbed[]
}

// Response from discord-send Edge Function
export interface DiscordSendResponse {
  success: boolean
  message_id?: string
  error?: string
}

// ============================================
// Apothecary System Types (v1.10.0)
// Crafting management for guild consumables
// ============================================

// Crafting request status
export type CraftingRequestStatus = 'pending' | 'in_progress' | 'completed'

// Crafting request
export interface CraftingRequest {
  id: string
  item_name: string
  loot_item_id: string | null
  quantity_requested: number
  quantity_completed: number
  quantity_claimed: number
  status: CraftingRequestStatus
  claimed_by: string | null
  claimed_at: string | null
  completed_by: string | null
  completed_at: string | null
  notes: string | null
  priority: number
  created_by: string | null
  created_at: string
  updated_at: string
}

// Extended crafting request with user info
export interface CraftingRequestWithUsers extends CraftingRequest {
  claimed_by_user?: { discord_username: string | null; discord_user_id: string | null } | null
  completed_by_user?: { discord_username: string | null; discord_user_id: string | null } | null
  created_by_user?: { discord_username: string | null; discord_user_id: string | null } | null
  loot_item?: LootItem | null
}

// Materials for guild buying list
export interface ApothecaryMaterial {
  id: string
  name: string
  loot_item_id: string | null
  price_each: number  // In copper (1g = 10000, 1s = 100)
  stack_size: number
  is_active: boolean
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

// Extended material with item info
export interface ApothecaryMaterialWithItem extends ApothecaryMaterial {
  loot_item?: LootItem | null
}

// Crafting history entry
export interface CraftingHistory {
  id: string
  request_id: string | null
  item_name: string
  loot_item_id: string | null
  quantity: number
  completed_by: string | null
  completed_at: string
}

// Extended crafting history with user info
export interface CraftingHistoryWithUser extends CraftingHistory {
  completed_by_user?: { discord_username: string | null; discord_user_id: string | null } | null
  loot_item?: LootItem | null
}

// Buying list item - items the guild is buying from members
export interface BuyingListItem {
  id: string
  loot_item_id: string
  price_percent: number      // Percentage of AH avg price (default 80%)
  price_override: number | null  // Optional manual price override in copper
  stack_size: number
  is_active: boolean
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

// Extended buying list item with loot item info
export interface BuyingListItemWithItem extends BuyingListItem {
  loot_item?: LootItem | null
}

// ============================================
// Player Craftable Items Types
// Tracks which items guild members can craft
// ============================================

export interface PlayerCraftableItem {
  id: string
  user_account_id: string
  loot_item_id: string
  is_alt: boolean
  alt_character_name: string | null
  created_at: string
  updated_at: string
}

export interface PlayerCraftableItemWithUser extends PlayerCraftableItem {
  user_account?: {
    id: string
    discord_username: string | null
    discord_avatar: string | null
    discord_user_id: string | null
  }
  loot_item?: LootItem
}

// ============================================
// Crafting Spells Types
// Spell/recipe data from addon (tradeskill scan + AtlasLoot capture)
// ============================================

export interface CraftingSpell {
  id: string
  spell_id: number
  name: string
  profession: string
  specialization: string | null
  game_version?: string
  result_item_id: string | null
  result_turtle_id: number | null
  result_item_count: number
  result_item_count_max: number
  result_type: 'item' | 'enchant'
  enchant_effect: string | null
  tools: string | null
  icon_name: string | null
  skill_orange: number | null
  skill_yellow: number | null
  skill_green: number | null
  skill_gray: number | null
  difficulty_type: string | null
  created_at: string
  updated_at: string
}

export interface CraftingSpellMaterial {
  id: string
  crafting_spell_id: string
  material_item_id: string | null
  material_turtle_id: number | null
  material_name: string
  quantity: number
  created_at: string
}

export interface CraftingSpellWithMaterials extends CraftingSpell {
  materials: (CraftingSpellMaterial & {
    material_item?: LootItem | null
  })[]
  result_item?: LootItem | null
}

// Addon spell data (from JSON export before DB import)
export interface AddonSpell {
  spell_id: number
  name: string
  profession: string
  icon_name: string | null
  result_item_id: number | null
  result_count_min: number
  result_count_max: number
  result_type: 'item' | 'enchant'
  enchant_effect: string | null
  tools: string | null
  skill_orange: number | null
  skill_yellow: number | null
  skill_green: number | null
  skill_gray: number | null
  difficulty_type: string | null
  reagents: AddonSpellReagent[]
}

export interface AddonSpellReagent {
  item_id: number | null
  name: string
  quantity: number
}

// Shopping list entry (player's crafting plans)
export interface ShoppingListEntry {
  id: string
  user_account_id: string
  crafting_spell_id: string
  quantity: number
  obtained_material_ids: string[]
  created_at: string
}

// Player knows a crafting spell (spell-centric crafter tracking)
export interface PlayerCraftableSpell {
  id: string
  user_account_id: string
  crafting_spell_id: string
  player_profile_id: string | null
  created_at: string
}

// Per-profession character selection (which character crafts each profession)
export interface PlayerProfessionCharacter {
  id: string
  user_account_id: string
  profession: string
  player_profile_id: string
  created_at: string
  updated_at: string
}

// ============================================
// Raid Bingo Types (Easter Egg)
// Hidden mini-game where players play bingo during raids
// ============================================

// Single cell on a bingo card
export interface BingoCardCell {
  index: number       // 0-24 (5x5 grid)
  quote: string       // The text to match, or 'FREE' for center
  marked: boolean     // Whether the cell has been marked
}

// Bingo game for a raid (one per raid)
export interface RaidBingoGame {
  id: string
  raid_event_id: string
  is_active: boolean
  winner_user_id: string | null
  winner_discord_user_id: string | null
  winner_character_name: string | null
  won_at: string | null
  announced_at: string | null
  created_at: string
  updated_at: string
}

// Player's bingo card
export interface RaidBingoCard {
  id: string
  game_id: string
  user_id: string
  cells: BingoCardCell[]  // 25 cells (5x5)
  has_bingo: boolean
  bingo_claimed_at: string | null
  created_at: string
  updated_at: string
}

// ============================================
// Guild Addons Types
// Recommended addons for guild members
// ============================================

// Guild addon entry
export interface GuildAddon {
  id: string
  name: string
  description: string | null
  category: string
  github_url: string | null
  download_url: string | null
  file_path: string | null      // Supabase storage path (latest version)
  file_name: string | null      // Original filename (latest version)
  file_size: number | null
  version: string | null         // Current version (e.g., "1.3.1"), only for uploaded addons
  is_mandatory: boolean
  installation_notes: string | null
  added_by: string | null
  created_at: string
  updated_at: string
  // Joined data
  added_by_user?: {
    discord_username: string | null
  }
}

// Addon file version history entry
export interface AddonVersion {
  id: string
  addon_id: string
  version: string
  file_path: string
  file_name: string
  file_size: number | null
  uploaded_by: string | null
  created_at: string
  // Joined data
  uploaded_by_user?: {
    discord_username: string | null
  }
}

// ============================================
// Class Guides Types
// Community-driven class/spec guides
// ============================================

export type GuideScope = 'general' | 'class' | 'spec'
export type GuideCategory = 'macro' | 'consume' | 'tip' | 'talent' | 'bis'

export interface GuideEntry {
  id: string
  scope: GuideScope
  wow_class: string | null
  spec: string | null
  category: GuideCategory
  title: string
  description: string | null
  macro_code: string | null
  consume_items: ConsumeItem[] | null
  bis_items: BisItem[] | null
  talent_url: string | null
  author_id: string
  upvote_count: number
  created_at: string
  updated_at: string
  // Joined fields
  author?: { discord_username: string; discord_avatar: string | null; discord_user_id: string | null }
  // Resolved from player_profiles (main character)
  author_character_name?: string | null
}

export interface ConsumeItem {
  name: string
  type: string
  slot?: string
  notes?: string
  loot_item_id?: string
  turtle_item_id?: number | null
  quality?: string | null
  icon_name?: string | null
  tooltip_html?: string | null
  tooltip_image_url?: string | null
}

export interface BisItem {
  slot: string              // 'head', 'neck', 'main hand', etc.
  name: string
  enchant?: string          // e.g. "Arcanum of Focus"
  enchant_effect?: string   // Spell effect text (for crafting spell enchants)
  enchant_turtle_item_id?: number | null  // Turtle item ID (for loot item enchants)
  enchant_spell_id?: string // UUID from crafting_spells (for rich spell tooltip)
  enchant_tooltip_html?: string | null   // Stored tooltip HTML (for loot item enchants)
  enchant_icon_name?: string | null      // Icon name (for loot item enchants)
  enchant_quality?: string | null        // Quality (for loot item enchants)
  notes?: string
  loot_item_id?: string     // UUID from loot_items (for wishlist integration)
  turtle_item_id?: number | null
  quality?: string | null
  icon_name?: string | null
  tooltip_html?: string | null
  tooltip_image_url?: string | null
}

export interface GuideUpvote {
  id: string
  entry_id: string
  user_account_id: string
  created_at: string
}

// Player absence (planned time away)
export interface PlayerAbsence {
  id: string
  user_account_id: string
  start_date: string   // 'YYYY-MM-DD'
  end_date: string
  reason: string | null
  created_at: string
}

// ── WoW Forever (launches 2026-11-04) roster status ─────────────────────────

export type RosterStatus = 'active' | 'break'
export type WowForeverFaction = 'Horde' | 'Alliance'
export type WowForeverRuleset = 'PvE' | 'PvP' | 'RP-PvE'

export interface MemberRosterStatus {
  user_account_id: string
  status: RosterStatus
  wow_forever_faction: WowForeverFaction | null
  wow_forever_ruleset: WowForeverRuleset | null
  /** On the WoW Forever beta right now, ahead of the live launch. */
  playing_beta: boolean
  /** Profiles this feature flipped to is_active = false, so a return restores exactly those. */
  paused_profile_ids: string[]
  set_by_officer: boolean
  created_at: string
  updated_at: string
}

// Re-export combat log types
export type {
  CombatLog,
  CombatLogEncounter,
  CombatLogCombatant,
  CombatLogPlayerStats,
  CombatLogAbilityStats,
  CombatLogDeath,
  CombatLogAuraStats,
  MeterType,
  MeterEntry,
  ViewMode,
  ViewerState,
  CombatEvent,
  CombatEventType,
  CombatLogLootEvent,
  ParseStatus,
  DeathRecapEntry,
} from './combatLog'
export { METER_LABELS } from './combatLog'

export const CLASS_SPECS: Record<WowClass, string[]> = {
  Warrior: ['Arms', 'Fury', 'Protection'],
  Paladin: ['Holy', 'Protection', 'Retribution'],
  Hunter: ['Beast Mastery', 'Marksmanship', 'Survival'],
  Rogue: ['Assassination', 'Combat', 'Subtlety'],
  Priest: ['Discipline', 'Holy', 'Shadow'],
  Shaman: ['Elemental', 'Enhancement', 'Restoration'],
  Mage: ['Arcane', 'Fire', 'Frost'],
  Warlock: ['Affliction', 'Demonology', 'Destruction'],
  Druid: ['Balance', 'Feral', 'Restoration'],
  'Death Knight': ['Blood', 'Frost', 'Unholy'],
}
